import React, { useState, useEffect } from 'react';
import { showNotification } from '../../utils/notification';
import { alertNotification } from '../../utils/notification';


/**
 * CategoryModal Component
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onSave
 * @param {Object} category (optional - edit mode)
 * @param {boolean} isSaving
 */
const CategoryModal = ({ isOpen, onClose, onSave, category, isSaving }) => {
  const MAX_ITEMS = 5;

  // 🔹 Form state
  const [categoryName, setCategoryName] = useState('');
  const [isActive, setIsActive] = useState(true);

  // 🔹 Table data
  const [categories, setCategories] = useState([]);

  // Populate data when editing (optional support)
  useEffect(() => {
    if (category) {
      setCategories([
        {
          name: category.name || '',
          isActive: category.isActive ?? true
        }
      ]);
    } else {
      setCategories([]);
    }

    setCategoryName('');
    setIsActive(true);
  }, [category, isOpen]);

  // ➕ Add category to table
  const addRow = () => {
    if (!categoryName.trim()) {
      alert('Category name is required');
      return;
    }

    if (categories.length >= MAX_ITEMS) {
      alert('Maximum 5 categories allowed');
      return;
    }

    // 🔍 Duplicate check (case-insensitive)
    const exists = categories.some(
      c => c.name.toLowerCase() === categoryName.trim().toLowerCase()
    );

    if (exists) {
       alertNotification('Category already exists' ,'error');
      return;
    }

    setCategories(prev => [
      ...prev,
      {
        name: categoryName.trim(),
        isActive
      }
    ]);

    // 🧹 Clear form
    setCategoryName('');
    setIsActive(true);
  };

  // ❌ Remove category
  const removeRow = (index) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
  };

  // 💾 Save categories
  const handleSubmit = (e) => {
    e.preventDefault();

    if (categories.length === 0) {
      alert('Please add at least one category');
      return;
    }

    onSave({ categories });
  };

  const handleClose = () => {
    if (!isSaving) onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="inventory-modal-overlay">
      <div className="inventory-modal-content"
  style={{ maxWidth: 500 }}
>

        {/* Header */}
        <header className="inventory-modal-header">
          <h3 className="inventory-modal-title">
            {category ? 'Edit Category' : 'Add New Category'}
          </h3>
          <button
            className="inventory-close-modal-btn"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </header>

        {/* Body */}
        <div className="inventory-modal-body">
          <form onSubmit={handleSubmit}>

            {/* FORM INPUTS */}
            <div className="inventory-form-grid">
              <label>Category Name *</label>
              <input
                type="text"
               className="inventory-custom-input"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                disabled={isSaving}
              />

              <label>Active</label>
              <input
                type="checkbox"
                className="inventory-custom-input"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSaving}
              />
             </div>

            <button
              type="button" 
              className="inventory-btn inventory-btn-action inventory-btn-edit "
              onClick={addRow}
              disabled={categories.length >= MAX_ITEMS || isSaving}
              style={{ marginLeft: 'auto', display: 'block' }}
            >
              ➕ Add
            </button>
           

            {/* TABLE */}
            <table className="inventory-data-table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Category Name</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {categories.length === 0 ? (
                  <tr>
                    <td colSpan="3">No data</td>
                  </tr>
                ) : (
                  categories.map((cat, index) => (
                    <tr key={index}>
                      <td>{cat.name}</td>
                      <td>{cat.isActive ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          disabled={isSaving}
                        >
                          ❌
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

          </form>
        </div>

        {/* Footer */}
        <footer className="inventory-modal-footer">
          <button
            className="inventory-btn inventory-btn-cancel"
            onClick={handleClose}
            disabled={isSaving}
            type="button"
          >
            Cancel
          </button>

          <button
            className="inventory-btn inventory-btn-save"
            onClick={handleSubmit}
            disabled={isSaving}
            type="button"
          >
            {isSaving ? 'Saving...' : 'Save Category'}
          </button>
        </footer>

      </div>
    </div>
  );
};

export default CategoryModal;
